'use client';

import type { ActivityWithRelations, MapField } from '@/utils/types';

import ActivityLaps from './ActivityLaps';
import ActivityStrengthSets from './ActivityStrengthSets';
import SportIcon from './SportIcon';
import { cn } from '@/utils/uiUtils';
import dynamic from 'next/dynamic';
import { format } from 'date-fns';
import { formatDistance } from '@trainme/api/client';
import { secondsToHm } from '@/utils/timeUtils';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';
import { trpc } from '@/app/api/trpc/client';
import { CoachSayCard } from '@/app/components/CoachSayCard';

const ActivityMap = dynamic(
  () => import('./ActivityMap').then((mod) => mod.ActivityMap),
  { ssr: false }
);

const formatStrengthDuration = (seconds: number) => {
  const rounded = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(rounded / 60);
  const remainingSeconds = rounded % 60;

  if (minutes === 0) return `${remainingSeconds}s`;
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m${remainingSeconds}s`;
};

type Props = {
  activity: ActivityWithRelations;
  className?: string;
};

export default function ActivityOne({ activity, className }: Props) {
  const unitSystem = useUnitSystem();
  const { data: detailActivity } = trpc.activities.getById.useQuery(
    { id: activity.id },
    {
      enabled: true,
      staleTime: 10_000,
    }
  );
  const { data: activityCoachSay } = trpc.analysis.getActivityAnalysis.useQuery(
    { activityId: activity.id },
    { enabled: true }
  );

  const displayActivity = detailActivity ?? activity;
  const strengthSets =
    displayActivity.strengthSets ?? activity.strengthSets ?? [];
  const isRestSet = (set: (typeof strengthSets)[number]) =>
    set.setType === 'rest' || set.isRest === true;
  const activeStrengthSets = strengthSets.filter((set) => !isRestSet(set));
  const restDuration = strengthSets.reduce(
    (sum, set) => (isRestSet(set) ? sum + (set.durationValue ?? 0) : sum),
    0
  );
  const groupedExercises = activeStrengthSets.reduce<
    Array<{
      name: string;
      sets: number;
      reps: number[];
      duration: number;
      firstSetIndex: number;
    }>
  >((groups, set, index) => {
    const name = set.exerciseName ?? set.exerciseCategory ?? 'Unknown';
    const existing = groups.find((group) => group.name === name);
    if (existing) {
      existing.sets += 1;
      existing.reps.push(set.repetitionValue ?? 0);
      existing.duration += set.durationValue ?? 0;
      return groups;
    }
    groups.push({
      name,
      sets: 1,
      reps: [set.repetitionValue ?? 0],
      duration: set.durationValue ?? 0,
      firstSetIndex: index,
    });
    return groups;
  }, []);
  const sortedExercises = [...groupedExercises].sort(
    (a, b) => a.firstSetIndex - b.firstSetIndex
  );
  const strengthTotals = groupedExercises.reduce(
    (totals, exercise) => ({
      sets: totals.sets + exercise.sets,
      reps: totals.reps + exercise.reps.reduce((sum, reps) => sum + reps, 0),
      duration: totals.duration + exercise.duration,
    }),
    { sets: 0, reps: 0, duration: 0 }
  );
  const isStrengthTraining =
    strengthSets.length > 0 || displayActivity.sportType === 'WeightTraining';
  return (
    <section
      className={cn([
        'flex min-h-full flex-col text-gray-800 dark:text-gray-200',
        className,
      ])}
    >
      <div className='flex items-center justify-between w-full gap-4 shrink-0 px-6 py-4'>
        <div className='flex-1 min-w-0 flex items-center gap-2'>
          <SportIcon type={displayActivity.sportType ?? undefined} />
          {displayActivity.startDate && (
            <div className='flex flex-col text-gray-500 whitespace-nowrap text-sm'>
              <span>
                {format(new Date(displayActivity.startDate), 'MMM d')}
              </span>
              <span>
                {format(new Date(displayActivity.startDate), 'h:mm a')}
              </span>
            </div>
          )}
        </div>
        {isStrengthTraining ? (
          <div className='flex-1 min-w-0 flex items-center justify-around gap-4 text-sm text-center'>
            <div>
              <div className='text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400'>
                Total time
              </div>
              <div className='font-medium'>
                {secondsToHm(displayActivity.movingTime) || 'n/a'}
              </div>
            </div>
            <div>
              <div className='text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400'>
                Working
              </div>
              <div className='font-medium'>
                {secondsToHm(strengthTotals.duration) || '0m'}
              </div>
            </div>
            <div>
              <div className='text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400'>
                Resting
              </div>
              <div className='font-medium'>
                {secondsToHm(restDuration) || '0m'}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='flex-1 min-w-0 text-sm text-center'>
              <div>
                Distance:{' '}
                {formatDistance(displayActivity.distance ?? null, unitSystem)}
              </div>
              <div>Duration: {secondsToHm(displayActivity.movingTime)}</div>
            </div>
            <div className='flex-1 min-w-0 text-sm flex flex-col gap-1 text-center'>
              <div>
                Avg Cadence:{' '}
                {displayActivity.averageCadence?.toFixed(0) || 'n/a'}
              </div>
              <div>
                Avg HR: {displayActivity.averageHeartrate?.toFixed(0) || 'n/a'}
              </div>
            </div>
            <div className='flex-none w-[120px] min-w-0 flex flex-col items-center gap-2'>
              {(function renderMetrics() {
                const metricDefinitions = [
                  {
                    label: 'RPE',
                    value: displayActivity.perceivedExertion,
                    max: 10,
                    barColor: 'bg-blue-500 dark:bg-blue-400',
                    formatValue: (value: number) => `${value.toFixed(1)}/10`,
                  },
                  {
                    label: 'Feeling',
                    value: displayActivity.sufferScore,
                    max: 100,
                    barColor: 'bg-green-500 dark:bg-green-400',
                    formatValue: (value: number) => `${value.toFixed(0)}/100`,
                  },
                ];

                return metricDefinitions.map((metric) => {
                  const valueIsNumber =
                    typeof metric.value === 'number' &&
                    !Number.isNaN(metric.value);
                  const percent = valueIsNumber
                    ? Math.min((metric.value! / metric.max) * 100, 100)
                    : 0;
                  const displayValue = valueIsNumber
                    ? metric.formatValue(metric.value!)
                    : 'n/a';

                  return (
                    <div
                      className='flex items-center gap-2 w-full text-xs'
                      key={metric.label}
                    >
                      <span className='w-12 shrink-0 text-center text-gray-500 dark:text-gray-400'>
                        {metric.label}
                      </span>
                      <div className='flex-1 max-w-[70px] bg-gray-300 dark:bg-gray-600 rounded-full h-2 overflow-hidden'>
                        <div
                          className={`${metric.barColor} h-2 rounded-full transition-all`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className='w-8 text-right font-medium text-gray-700 dark:text-gray-300'>
                        {displayValue}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          </>
        )}
      </div>

      <div className='flex-1 flex min-h-0 flex-col lg:grid lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.85fr)] lg:gap-2'>
        <div className='flex flex-1 min-h-0 flex-col'>
          <div className='px-6 flex-1 min-h-0 flex flex-col'>
            <div className='w-full h-full min-h-[14rem] bg-slate-200 dark:bg-slate-800 relative rounded-lg overflow-hidden shadow-inner'>
              {isStrengthTraining ? (
                <div className='flex h-full flex-col gap-4 px-4 py-4'>
                  <div className='min-h-[19rem] flex-1 overflow-y-auto'>
                    <div className='grid grid-cols-[minmax(120px,1fr)_48px_72px_72px_minmax(0,3fr)] items-center gap-3 border-b border-gray-200 px-4 py-2 text-sm font-bold uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400'>
                      <div>Exercise</div>
                      <div>Sets</div>
                      <div>Total reps</div>
                      <div>Total duration</div>
                      <div>Reps</div>
                    </div>
                    <div className='divide-y divide-gray-100 dark:divide-gray-800'>
                      <div className='grid grid-cols-[minmax(120px,1fr)_48px_72px_72px_minmax(0,3fr)] items-start gap-3 bg-teal-50/70 px-4 py-2 dark:bg-teal-950/20'>
                        <div className='font-semibold text-teal-900 dark:text-teal-100'>
                          Total
                        </div>
                        <div className='pt-0.5 text-sm font-medium text-teal-800 dark:text-teal-200'>
                          {strengthTotals.sets}
                        </div>
                        <div className='pt-0.5 text-sm font-medium text-teal-800 dark:text-teal-200'>
                          {strengthTotals.reps}
                        </div>
                        <div className='pt-0.5 text-sm font-medium text-teal-800 dark:text-teal-200'>
                          {formatStrengthDuration(strengthTotals.duration)}
                        </div>
                        <div className='flex flex-wrap gap-2 text-sm text-teal-700 dark:text-teal-300'>
                          <span className='whitespace-nowrap rounded-full bg-teal-100 px-3 py-1 leading-none dark:bg-teal-900/40'>
                            {strengthTotals.reps}
                          </span>
                        </div>
                      </div>
                      {sortedExercises.map((exercise) => (
                        <div
                          className='grid grid-cols-[minmax(120px,1fr)_48px_72px_72px_minmax(0,3fr)] items-start gap-3 px-4 py-2'
                          key={exercise.name}
                        >
                          <div className='font-semibold text-gray-900 dark:text-gray-100'>
                            {exercise.name}
                          </div>
                          <div className='pt-0.5 text-sm font-medium text-gray-600 dark:text-gray-300'>
                            {exercise.sets}
                          </div>
                          <div className='pt-0.5 text-sm font-medium text-gray-600 dark:text-gray-300'>
                            {exercise.reps.reduce((sum, reps) => sum + reps, 0)}
                          </div>
                          <div className='pt-0.5 text-sm font-medium text-gray-600 dark:text-gray-300'>
                            {formatStrengthDuration(exercise.duration)}
                          </div>
                          <div className='flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-300'>
                            {exercise.reps.map((reps, index) => (
                              <span
                                className='whitespace-nowrap rounded-full bg-gray-100 px-3 py-1 leading-none dark:bg-gray-800'
                                key={`${exercise.name}-${index}`}
                              >
                                {reps}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : activity.mapField && typeof activity.mapField === 'object' ? (
                <ActivityMap
                  key={displayActivity.id}
                  mapField={displayActivity.mapField as MapField}
                />
              ) : (
                <div className='flex items-center justify-center h-full text-gray-400 italic text-sm'>
                  No map data available
                </div>
              )}
            </div>
          </div>
          <div className='flex-[0.7] min-h-[160px] flex flex-col overflow-hidden pl-10 pr-6 pt-4 pb-6'>
            {isStrengthTraining ? (
              <ActivityStrengthSets
                strengthSets={displayActivity.strengthSets || []}
              />
            ) : (
              <ActivityLaps laps={displayActivity.laps || []} />
            )}
          </div>
        </div>
        {activityCoachSay && (
          <div className='min-h-0 min-w-0 self-stretch overflow-y-auto px-6 pb-4 pt-4 lg:pt-0 lg:pl-0'>
            <CoachSayCard
              coachSay={activityCoachSay.coachSay}
              trainmeScore={activityCoachSay.trainmeScore}
              coachMessageId={activityCoachSay.coachMessageId}
            />
          </div>
        )}
      </div>
    </section>
  );
}
